import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Add, Event, Hotel } from '@mui/icons-material';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Typography } from '@mui/material';
import { groupsApi } from '../../services/groups-api-client';
import type { GroupSummary } from '../../types/groups';
import { plural } from '../../lib/plural';

export default function GroupList() {
  const navigate = useNavigate();
  const { data = [], isLoading, error } = useQuery<GroupSummary[]>('groups', groupsApi.listGroups);

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Groups</Typography>
          <Typography color="text.secondary">Create event pages with itinerary tiles, room blocks, and concierge handoff.</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/groups/new')}>New Group</Button>
      </Box>
      {error ? <Alert severity="error" sx={{ mb: 2 }}>Could not load groups.</Alert> : null}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
        {data.map((group) => (
          <Card key={group.id} onClick={() => navigate(`/groups/${group.id}`)} sx={{ cursor: 'pointer', borderRadius: 3, '&:hover': { borderColor: 'primary.main' }, border: '1px solid #E5E7EB' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight={800}>{group.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{group.locationName || group.slug}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <Chip size="small" label={group.published ? 'Published' : 'Draft'} color={group.published ? 'success' : 'default'} />
                  <Chip size="small" label={group.accessMode === 'invite_only' ? 'Invite-only' : 'Public'} color={group.accessMode === 'invite_only' ? 'warning' : 'default'} />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                <Chip size="small" icon={<Event />} label={plural(group.roomRequestCount, 'request')} />
                <Chip size="small" icon={<Hotel />} label={`${plural(group.heldRoomCount, 'room')} held`} />
                <Chip size="small" label={`${group.bookedRoomCount} booked`} />
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
      {!data.length ? (
        <Card sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Typography fontWeight={700}>No groups yet</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>Start with a test event page and room block.</Typography>
          <Button variant="contained" onClick={() => navigate('/groups/new')}>Create Group</Button>
        </Card>
      ) : null}
    </Box>
  );
}
